import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  styled,
  Theme,
} from '@material-ui/core';

type Props = {
  title?: string;
  description?: string;
  open?: boolean;
  onClose: () => void;
  onClickConfirm?: () => void;
  confirmationText?: string;
};

const CustomDialog = styled(Dialog)({
  borderRadius: 20,
  '& .MuiDialog-paperWidthSm': {
    marginBottom: 'auto',
    marginTop: '0 !important',
    borderRadius: '0 0 10px 10px',
    padding: '1rem',
  },
});

const CustomMuiButton = styled(MuiButton)<Theme>(({ theme }) => ({
  background: theme.palette.ternary.main,
  color: theme.palette.common.white,
  padding: theme.spacing(1, 2),
  fontSize: theme.typography.h4.fontSize,
  fontWeight: 600,
}));

const NotificationDialog: FC<Props> = ({
  title,
  description,
  open,
  onClose,
  onClickConfirm,
  confirmationText = undefined,
}) => {
  const { t } = useTranslation();

  const handleConfirmClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (onClickConfirm) {
      onClickConfirm();
      onClose();
      return;
    }
    onClose();
  };

  return (
    <CustomDialog
      open={open || false}
      onClose={(event, reason) => {
        // Request user to click on the confirmation button to close the dialog
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      aria-labelledby={title && 'notification-dialog-title'}
      aria-describedby="notification-dialog-description"
    >
      {title && <DialogTitle id="notification-dialog-title">{title}</DialogTitle>}
      {description && (
        <DialogContent>
          <DialogContentText
            style={{ fontSize: 16, fontWeight: 400, color: '#000000' }}
            id="notification-dialog-description"
          >
            {description}
          </DialogContentText>
        </DialogContent>
      )}
      <DialogActions>
        <CustomMuiButton
          variant="contained"
          onClick={handleConfirmClick}
          disableElevation={true}
          autoFocus
        >
          {confirmationText || t('shared.forms.confirm')}
        </CustomMuiButton>
      </DialogActions>
    </CustomDialog>
  );
};

export default NotificationDialog;
